import {
  type ITableCell,
  type ITableSettings,
  tableNormalizeColWidths,
} from "@library-elements/table";

// Ensure tableColWidths is an array of exactly `tableCols` finite entries.
// Without this, a missing/NaN/short array corrupts downstream splices and
// causes cells to render with auto width (e.g. after CSV import followed
// by an add-column — the table stretches past its container).
const ensureColWidths = (draft: ITableSettings): number[] => {
  const { tableCols } = draft;
  const current = draft.tableColWidths ?? [];
  const isValid =
    current.length === tableCols &&
    current.every((w) => Number.isFinite(w) && w > 0);
  if (isValid) return current;
  return Array.from({ length: tableCols }, () => 100 / tableCols);
};
import { type RootState, store } from "@store";
import { setTableActiveCellsIds } from "@store/story";
import { getTableActiveStartCellId } from "@store/story/selectors";
import { TABLE_MAX_COLS } from "../constants";
import {
  copyManyTableCellBorders,
  copyTableCellBorders,
} from "./copy-table-cell-borders";
import { editTableEntity } from "./edit-table-entity";
import { makeTableEmptyCell } from "./make-table-empty-cell";
import {
  adjustMergesForColDelete,
  adjustMergesForColInsert,
  markInsertedCellsAsMerged,
} from "./table-cell-merge-actions";
import { getTableDeleteTargetIdxs, type IOpts } from "./table-cell-row-actions";

export const tableRemoveActiveCellCol = (opts?: IOpts) => {
  editTableEntity(
    (draft, state) => {
      const { tableCols } = draft;
      const cols = new Set(
        getTableDeleteTargetIdxs(draft.tableData, state, opts).map(
          (idx) => idx % tableCols,
        ),
      );
      if (cols.size === 0) return;

      // Right-to-left, so the remaining target columns keep their indexes.
      for (const col of Array.from(cols).sort((a, b) => b - a)) {
        removeColFromDraft(draft, col);
      }
    },
    { entityId: opts?.entityId },
  );

  store.dispatch(setTableActiveCellsIds([]));
};

export const removeColFromDraft = (draft: ITableSettings, cellCol: number) => {
  const { tableData, tableCols, tableRows } = draft;

  // Adjust merge regions before removing the column
  adjustMergesForColDelete(draft, cellCol);

  for (let i = 0; i < tableRows; i++) {
    const removedCellIdx = cellCol + i * tableCols - i;
    const removedCell = tableData[removedCellIdx];
    tableData.splice(removedCellIdx, 1);

    const nextCell = tableData[removedCellIdx];
    if (cellCol < tableCols - 1 && nextCell) {
      copyTableCellBorders(nextCell, "Left", removedCell);
    }

    const prevCell = tableData[removedCellIdx - 1];
    if (cellCol > 0 && prevCell) {
      copyTableCellBorders(prevCell, "Right", removedCell, "Left");
    }
  }

  const newColsWidths = [...ensureColWidths(draft)];
  newColsWidths.splice(cellCol, 1);
  draft.tableColWidths = tableNormalizeColWidths(newColsWidths);
  draft.tableCols = Math.max(0, draft.tableCols - 1);
};

interface IAddColToEntityProps {
  draft: ITableSettings;
  state: RootState;
  addAfter: boolean;
  cellId?: string;
}

export const tableAddColToEntity = ({
  cellId,
  draft,
  state,
  addAfter,
}: IAddColToEntityProps) => {
  const activeCellId = cellId || getTableActiveStartCellId(state);
  const { tableData, tableCols, tableRows } = draft;

  if (tableCols >= TABLE_MAX_COLS) {
    return;
  }

  const activeCellIdx = tableData.findIndex((el) => el.id === activeCellId);
  if (activeCellIdx < 0) return;
  const cellCol = activeCellIdx % tableCols;

  // Adjust merge regions before inserting the column
  const insertCol = cellCol + (addAfter ? 1 : 0);
  adjustMergesForColInsert(draft, insertCol);

  const newCells: ITableCell[] = [];
  for (let i = 0; i < tableRows; i++) {
    newCells.push(makeTableEmptyCell());
    const newCellIdx = cellCol + i * tableCols + (i + (addAfter ? 1 : 0));
    tableData.splice(newCellIdx, 0, makeTableEmptyCell());
  }

  for (let i = 0; i < tableRows; i++) {
    const newCellIdx = cellCol + i * tableCols + (i + (addAfter ? 1 : 0));
    const newCell = tableData[newCellIdx];

    if (addAfter) {
      const prevCell = tableData[newCellIdx - 1];
      if (prevCell) {
        copyTableCellBorders(newCell, "Left", prevCell, "Right");

        copyManyTableCellBorders(newCell, prevCell, ["Top", "Bottom", "Right"]);
      }
    }

    if (!addAfter) {
      const nextCell = tableData[newCellIdx + 1];
      if (nextCell) {
        copyTableCellBorders(newCell, "Right", nextCell, "Left");
        copyManyTableCellBorders(newCell, nextCell, ["Top", "Bottom", "Left"]);
      }
    }
  }

  // Give the new col the average share (100 / newTotal) and scale the existing
  // cols down by the same factor, so the widths the author dragged into place
  // keep their relative proportions. Because the existing widths sum to 100,
  // scaling them by oldTotal/newTotal frees up exactly 100/newTotal for the
  // new col — no normalize pass needed and the new col is a true average, not
  // the slightly-too-narrow one a scale-then-normalize would produce.
  // (Rebuilding every col as equal, as this used to, silently discarded every
  // manual column resize on the table.)
  const newTotal = tableCols + 1;
  const scale = tableCols / newTotal;
  const nextWidths = ensureColWidths(draft).map((w) => w * scale);
  nextWidths.splice(insertCol, 0, 100 / newTotal);
  draft.tableColWidths = tableNormalizeColWidths(nextWidths);
  draft.tableCols += 1;

  // Mark newly inserted cells that fall inside merge regions
  markInsertedCellsAsMerged(draft);
};

const tableAddCol = (addAfter: boolean, opts?: IOpts) => {
  editTableEntity(
    (draft, state) => {
      tableAddColToEntity({ draft, state, addAfter, cellId: opts?.cellId });
    },
    { entityId: opts?.entityId },
  );
};

export const tableAddColAfterActiveCell = (opts?: IOpts) => {
  tableAddCol(true, opts);
};

export const tableAddColBeforeActiveCell = (opts?: IOpts) => {
  tableAddCol(false, opts);
};

export const tableDuplicateCol = (opts?: IOpts) => {
  editTableEntity(
    (draft, state) => {
      const activeCellId = opts?.cellId || getTableActiveStartCellId(state);
      const { tableData, tableCols, tableRows } = draft;

      if (tableCols >= TABLE_MAX_COLS) return;

      const activeCellIdx = tableData.findIndex((el) => el.id === activeCellId);
      if (activeCellIdx < 0) return;

      const cellCol = activeCellIdx % tableCols;

      // Adjust merge regions before inserting the column
      adjustMergesForColInsert(draft, cellCol + 1);

      for (let i = 0; i < tableRows; i++) {
        const sourceIdx = cellCol + i * (tableCols + 1);
        const sourceCell = tableData[sourceIdx];
        if (!sourceCell) continue;
        const newCell: ITableCell = {
          ...sourceCell,
          id: makeTableEmptyCell().id,
          mergedInto: undefined,
          colSpan: 1,
          rowSpan: 1,
        };
        tableData.splice(sourceIdx + 1, 0, newCell);
      }

      const newColsWidths = [...ensureColWidths(draft)];
      newColsWidths.splice(cellCol + 1, 0, newColsWidths[cellCol]);
      draft.tableColWidths = tableNormalizeColWidths(newColsWidths);
      draft.tableCols += 1;

      markInsertedCellsAsMerged(draft);
    },
    { entityId: opts?.entityId },
  );
};
